import { ProjectType } from "../../../../clients/graphqlTypes.js";
import {
  initialOnboardingFlowWizardState,
  type MatrixSelection,
  type OfferedServicesCommitPath,
  type OfferedServicesPayload,
  type OnboardingFlowWizardAction,
  type OnboardingFlowWizardState,
} from "./onboardingFlowWizardState.js";

function toggleId(arr: string[], id: string): string[] {
  return arr.includes(id) ? arr.filter(x => x !== id) : [...arr, id];
}

function toggleProjectType(
  arr: ProjectType[],
  projectType: ProjectType
): ProjectType[] {
  return arr.includes(projectType)
    ? arr.filter(x => x !== projectType)
    : [...arr, projectType];
}

function matrixCellExists(
  arr: MatrixSelection[],
  categoryId: string,
  projectType: ProjectType
): boolean {
  return arr.some(
    s => s.categoryId === categoryId && s.projectType === projectType
  );
}

export function onboardingFlowWizardReducer(
  state: OnboardingFlowWizardState,
  action: OnboardingFlowWizardAction
): OnboardingFlowWizardState {
  switch (action.type) {
    case "toggleVertical":
      // Changing verticals invalidates the matrix prefill (declared rows +
      // cells were derived from the old vertical set). Clear matrix state so
      // re-entry to the matrix stage runs prefill against the new verticals.
      return {
        ...state,
        selectedVerticals: toggleId(state.selectedVerticals, action.categoryId),
        matrixDeclaredCategories: [],
        matrixSelections: [],
      };

    case "toggleProjectType":
      // Same reasoning as toggleVertical — changing project types invalidates
      // the matrix prefill's cell cross-product.
      return {
        ...state,
        selectedProjectTypes: toggleProjectType(
          state.selectedProjectTypes,
          action.projectType
        ),
        matrixDeclaredCategories: [],
        matrixSelections: [],
      };

    case "setApplyToAll":
      // Picking "yes" makes the specify-mode + matrix branch irrelevant.
      // Clear those so a user who backtracks doesn't carry stale state forward.
      if (action.value === "yes") {
        return {
          ...state,
          applyToAll: "yes",
          specifyMode: "unset",
          matrixDeclaredCategories: [],
          matrixSelections: [],
        };
      }
      return { ...state, applyToAll: "no" };

    case "setSpecifyMode":
      // Picking "later" makes any prior matrix state moot — clear it.
      if (action.value === "later") {
        return {
          ...state,
          specifyMode: "later",
          matrixDeclaredCategories: [],
          matrixSelections: [],
        };
      }
      return { ...state, specifyMode: "now" };

    case "toggleMatrixCell": {
      const next = matrixCellExists(
        state.matrixSelections,
        action.categoryId,
        action.projectType
      )
        ? state.matrixSelections.filter(
            s =>
              !(
                s.categoryId === action.categoryId &&
                s.projectType === action.projectType
              )
          )
        : [
            ...state.matrixSelections,
            { categoryId: action.categoryId, projectType: action.projectType },
          ];
      return { ...state, matrixSelections: next };
    }

    case "setMatrixCellsForCategoriesAndType": {
      // Column-cascade: set/unset every (cat, action.projectType) for each
      // cat in action.categoryIds. Used by parent-checkbox clicks.
      const idSet = new Set(action.categoryIds);
      const withoutTouched = state.matrixSelections.filter(
        s => !(idSet.has(s.categoryId) && s.projectType === action.projectType)
      );
      const added = action.checked
        ? action.categoryIds.map(categoryId => ({
            categoryId,
            projectType: action.projectType,
          }))
        : [];
      return { ...state, matrixSelections: [...withoutTouched, ...added] };
    }

    case "toggleMatrixDeclaredCategory": {
      const declared = state.matrixDeclaredCategories.includes(
        action.categoryId
      );
      if (declared) {
        // Undeclaring a row also clears its own cells. Per design, this does
        // NOT cascade to descendants — sibling/child rows are independent.
        return {
          ...state,
          matrixDeclaredCategories: state.matrixDeclaredCategories.filter(
            id => id !== action.categoryId
          ),
          matrixSelections: state.matrixSelections.filter(
            s => s.categoryId !== action.categoryId
          ),
        };
      }
      return {
        ...state,
        matrixDeclaredCategories: [
          ...state.matrixDeclaredCategories,
          action.categoryId,
        ],
      };
    }

    case "setMatrixDeclaredCategories": {
      // Replace the declared-row set AND drop any orphaned cells (cells whose
      // category is no longer declared). The matrix UI drives this via
      // rowSelectionModel; the cell-filter keeps the invariant "any cell
      // implies its category is declared" without forcing the UI to compute
      // a per-id diff.
      const declaredSet = new Set(action.categoryIds);
      return {
        ...state,
        matrixDeclaredCategories: Array.from(declaredSet),
        matrixSelections: state.matrixSelections.filter(s =>
          declaredSet.has(s.categoryId)
        ),
      };
    }

    case "reset":
      return initialOnboardingFlowWizardState;
  }
}

// Path → payload selector. Maps the wizard state + the chosen commit path
// to the exact { categories, services } shape consumed by
// setOrganisationOfferedServices.
export function selectOfferedServicesPayload(
  state: OnboardingFlowWizardState,
  path: OfferedServicesCommitPath
): OfferedServicesPayload {
  switch (path) {
    case "skip-project-types":
    case "specify-later":
    case "skip-matrix":
      return { categories: state.selectedVerticals, services: [] };

    case "apply-to-all-yes": {
      const services = state.selectedVerticals.flatMap(categoryId =>
        state.selectedProjectTypes.map(projectType => ({
          categoryId,
          projectType,
        }))
      );
      return { categories: state.selectedVerticals, services };
    }

    case "specify-now-commit": {
      // `categories` is the declared-row set the user explicitly toggled in
      // the matrix view. Cells require their row to be declared (UI guards
      // this), so any categoryId in matrixSelections is already present in
      // matrixDeclaredCategories — but we union defensively in case state
      // diverges (e.g. seeded test fixtures).
      const categories = Array.from(
        new Set([
          ...state.matrixDeclaredCategories,
          ...state.matrixSelections.map(s => s.categoryId),
        ])
      );
      const services = state.matrixSelections.map(s => ({
        categoryId: s.categoryId,
        projectType: s.projectType,
      }));
      return { categories, services };
    }
  }
}
